/**
 * Os eventos em tempo real do chat (ADR-0017): validados contra o contrato antes de entrarem na cache.
 *
 * O WebSocket fica fora do cliente tipado, por isso cada evento passa pelo zod gerado do OpenAPI
 * (`apiResponseSchema("ChatRealtimeEvent")`). Um evento que não valide é recusado e registado, sem partir a conversa:
 * a base é a fonte de verdade e a próxima retoma por cursor repõe o que faltar.
 */

import type { ApiValidatedResponse } from "@/types/api.zod";

export type ChatRealtimeEvent = ApiValidatedResponse["ChatRealtimeEvent"];
export type ChatEventType = ChatRealtimeEvent["type"];
export type ChatEventOf<T extends ChatEventType> = Extract<ChatRealtimeEvent, { type: T }>;

/** Versão do protocolo que este web compreende. Um evento de outra versão é recusado (o servidor é mais novo). */
const CHAT_PROTOCOL_VERSION = 1;

type EventSchema = { safeParse: (data: unknown) => { success: true; data: ChatRealtimeEvent } | { success: false; error: { issues: Array<{ message?: string }> } } };
let schema: EventSchema | null = null;

/**
 * Carrega o validador do contrato. Os esquemas gerados pesam centenas de KB e o sino do cabeçalho importa este módulo no
 * arranque de TODAS as páginas: por isso o validador vem num pedaço à parte, só quando a ligação em tempo real vai abrir
 * (quem não tem o chat nunca o descarrega).
 */
export async function loadEventValidator(): Promise<void> {
  if (schema) return;
  const { apiResponseSchema } = await import("@/types/api.zod");
  schema = apiResponseSchema("ChatRealtimeEvent") as unknown as EventSchema;
}

export const eventValidatorReady = (): boolean => schema !== null;

export type ParseResult = { ok: true; event: ChatRealtimeEvent } | { ok: false; reason: string };

export function parseRealtimeEvent(raw: string): ParseResult {
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return { ok: false, reason: "não é JSON" };
  }
  // Sem o validador carregado nada entra na cache: a ligação só abre depois de `loadEventValidator`.
  if (!schema) return { ok: false, reason: "validador por carregar" };
  const result = schema.safeParse(data);
  if (!result.success) return { ok: false, reason: result.error.issues[0]?.message ?? "não valida" };
  if (result.data.v !== CHAT_PROTOCOL_VERSION) return { ok: false, reason: `versão ${String(result.data.v)}` };
  return { ok: true, event: result.data };
}
